import { Fragment, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'
import { Button, Loader } from '../../components'
import { resolveCycleCount } from '../../services/cycleCountServices'
import { localizeReviewError } from './reviewErrors'

const T_PREFIX = 'cycle_counts.resolve_modal'

// One row per product across the counter's and the reviewer's results.
export const buildComparison = location => {
  const byId = {}
  ;(location?.results || []).forEach(r => {
    byId[r.product_id] = { product_id: r.product_id, product_name: r.product_name, product_sku: r.product_sku,
      system_at_count: r.system_quantity, counter: r.counted_quantity, system_now: null, reviewer: null }
  })
  ;(location?.reviewer_results || []).forEach(r => {
    byId[r.product_id] = { ...(byId[r.product_id] || { product_id: r.product_id, product_name: r.product_name, product_sku: r.product_sku, system_at_count: null, counter: null }),
      system_now: r.system_quantity, reviewer: r.counted_quantity }
  })
  return Object.values(byId)
}

export const ResolveReviewDialog = ({ open, location, onSuccess = () => {}, onCancel = () => {} }) => {
  const { i18n } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const rows = useMemo(() => buildComparison(location), [location])

  useEffect(() => {
    if (!open) {
      setBusy(false)
      setErrorMessage(null)
    }
  }, [open])

  const onConfirm = async () => {
    if (busy) return
    if (!location?.id) return
    setBusy(true)
    setErrorMessage(null)
    let result
    try {
      result = await resolveCycleCount(location.id)
    } catch (error) {
      setBusy(false)
      const err = error.response?.data?.error
      const errorCode = error.response?.data?.error_code
      const rawMsg = typeof err === 'string' ? err : err?.message || i18n.t(`${T_PREFIX}.error`)
      setErrorMessage(localizeReviewError(rawMsg, errorCode))
      return
    }
    setBusy(false)
    onSuccess(result)
  }

  const cell = v => (v == null ? '-' : v)

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => {}}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-3xl sm:w-full p-6 text-gray-600 my-3">
              <Loader show={busy} />
              <div className="absolute top-0 right-0 p-5 cursor-pointer" onClick={onCancel}>
                <XIcon className="h-5 w-5" />
              </div>
              <Dialog.Title className="text-lg text-center mb-2">{i18n.t(`${T_PREFIX}.title`)}</Dialog.Title>
              <p className="text-sm text-center text-gray-500 mb-4">
                {i18n.t(`${T_PREFIX}.summary`, { location: location?.location_code, attempts: location?.review_attempts ?? 0 })}
              </p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1 pr-2">{i18n.t(`${T_PREFIX}.product`)}</th>
                    <th className="py-1 px-2 text-right">{i18n.t(`${T_PREFIX}.system_at_count`)}</th>
                    <th className="py-1 px-2 text-right">{i18n.t(`${T_PREFIX}.counter`)}</th>
                    <th className="py-1 px-2 text-right">{i18n.t(`${T_PREFIX}.system_now`)}</th>
                    <th className="py-1 px-2 text-right">{i18n.t(`${T_PREFIX}.reviewer`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.product_id} className="border-t">
                      <td className="py-1 pr-2">
                        <div>{r.product_name || `#${r.product_id}`}</div>
                        {r.product_sku && <div className="text-xs text-gray-400">{r.product_sku}</div>}
                      </td>
                      <td className="py-1 px-2 text-right">{cell(r.system_at_count)}</td>
                      <td className="py-1 px-2 text-right">{cell(r.counter)}</td>
                      <td className="py-1 px-2 text-right">{cell(r.system_now)}</td>
                      <td className={`py-1 px-2 text-right ${r.reviewer == null || r.reviewer !== r.system_now ? 'text-red-600 font-medium' : ''}`}>{cell(r.reviewer)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {errorMessage && <p className="text-sm text-red-600 mt-3 text-center">{errorMessage}</p>}
              <div className="mt-4 flex justify-between">
                <Button className="m-1" type="secondary" onClick={onCancel} disabled={busy}>{i18n.t(`${T_PREFIX}.cancel`)}</Button>
                <Button className="m-1" type="primary" onClick={onConfirm} disabled={busy} loading={busy}>{i18n.t(`${T_PREFIX}.confirm`)}</Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
